import React, { ReactNode, useCallback, useState, useEffect } from "react";
import { TableVirtuoso } from "react-virtuoso";
import update from "immutability-helper";
import { useAddOrUpdateTagMutation } from "features/api/contentManagement/contentManagementApiSlice";
import ContentListItem from "./ContentListItem";

type IColumn<T> = {
  title: string;
  dataIndex: number | string;
  key: string;
  flex: number;
  renderCell: (cell: string | boolean | number | T) => JSX.Element;
  asc: boolean;
  minWidth: number;
  maxWidth?: number;
};

interface RenderListProps<T> {
  columns: IColumn<T>[];
  data: T[];
  disableField?: string;
  isDraggable?: boolean;
}

const RenderList = <T,>({
  columns = [],
  data = [],
  disableField,
  isDraggable = false,
}: RenderListProps<T>) => {
  const [addOrUpdateTag] = useAddOrUpdateTagMutation();
  const [hasDropped, setHasDropped] = useState(false);
  const [rows, setRows] = useState<T[]>(
    data?.map((item) => ({
      ...item,
    }))
  );

  useEffect(() => {
    setRows(
      data?.map((item) => ({
        ...item,
      }))
    );
  }, [data]);

  const handleDrop = useCallback(() => {
    setHasDropped(true);
  }, []);

  useEffect(() => {
    if (hasDropped) {
      setHasDropped(false);
      addOrUpdateTag(rows).unwrap();
    }
  }, [addOrUpdateTag, rows, hasDropped]);

  const moveItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const draggedRow = rows[dragIndex];
      const updatedRows = update(rows, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, draggedRow],
        ],
      });

      // Update the index properties of the items
      const updatedRowsWithIndexes = updatedRows.map((item, index) => ({
        ...item,
        index,
      }));

      setRows(updatedRowsWithIndexes);
    },
    [rows]
  );

  const renderHeader = useCallback((): ReactNode => {
    return (
      <tr>
        {columns.map((column) => (
          <th
            style={{
              minWidth: column.minWidth,
              flex: column.flex,
              maxWidth: column?.maxWidth,
            }}
            key={column.dataIndex}
          >
            {column.title}
          </th>
        ))}
      </tr>
    );
  }, [columns]);

  const renderItems = useCallback(
    (index: number, item: T) => {
      return (
        <ContentListItem
          moveItem={isDraggable ? moveItem : null}
          index={index}
          key={index}
          type="row"
          isDisabled={item[disableField]}
        >
          {columns.map((column) => (
            <td
              style={{
                minWidth: column.minWidth,
                maxWidth: column?.maxWidth,
                flex: column.flex,
              }}
              key={column.dataIndex}
            >
              {column.renderCell(
                column.key === "actions" ? item : item[column.key]
              )}
            </td>
          ))}
        </ContentListItem>
      );
    },
    [columns, moveItem, disableField, isDraggable]
  );

  return (
    <TableVirtuoso
      data={rows}
      useWindowScroll
      fixedHeaderContent={renderHeader}
      itemContent={renderItems}
      onDrop={handleDrop}
    />
  );
};

export default RenderList;
